// src/pos-app/OperatorLockScreen.js
//
// F12a Task 7 — full-screen operator PIN lock / shift-handover overlay for the
// native POS stations (visual reference: docs/mockups/f12a/cashier-station.html,
// lock overlay). Shown whenever the station has no active operator — including
// the very first entry after boot (device boots → station → locked → PIN).
//
// Deliberately presentational: PIN state lives here, but validation is the
// parent's job via `onSubmit(pin)` (CashierStation passes
// useOperatorSession.unlock). `onSubmit` must resolve truthy on success and
// falsy on failure — on failure the pad clears and shakes. Any valid employee
// PIN signs that employee in as the new operator; that IS shift handover, so
// there is no user picker and no dismiss affordance (a lock screen cannot be
// escaped, only unlocked).
//
// Aurora Glass, intensity full (idle/lock moment). All colors from --c-* vars
// so light/dark themes both work; RTL-safe (logical properties only).
//
// F12a-N task-n8 Bug B — portaled to document.body (same pattern as
// TopBarExtras' More menu / CartPanel's promo sheet — 1cbf151 / 9ccec27).
// Root cause: wherever a caller mounts this (CashierStation/WaiterStation
// render it inside <AuroraBackground>), it used to sit under `.aur-root`,
// which is `position:fixed` with NO explicit z-index. `position:fixed`
// ALWAYS opens its own stacking context regardless of z-index, so
// `.aur-root` (and everything nested inside it, including this screen's own
// z-index:70) only ever competes at z-index:auto/0 against siblings OUTSIDE
// `.aur-root` — this screen's z-index:70 only won comparisons happening
// INSIDE that trapped context. Meanwhile the More menu / promo sheet are
// portaled straight to document.body with explicit z-index (60/50) to
// escape an analogous trap (a backdrop-filter ancestor) — but neither
// auto-closes on lock, so a menu/sheet left open at lock time kept its
// full-viewport scrim mounted in document.body, which — thanks to this
// asymmetry — painted ABOVE the entire AuroraBackground/lock-screen layer
// no matter how high z-index:70 looked on paper, silently swallowing every
// tap meant for the PIN pad. Portaling this screen to document.body too
// puts it in the same real stacking context as those overlays, where its
// z-index (already the highest of any of them: 70 > 60/59/50/40/45/44/48)
// actually wins.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import AuroraBackground from './shell/AuroraBackground';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const PIN_LENGTH = 6;

const CSS = `
.ols-wrap{flex:1;display:flex;align-items:center;justify-content:center;box-sizing:border-box;
  padding:calc(20px + var(--safe-top)) 20px calc(20px + var(--safe-bottom));overflow-y:auto;}
.ols-card{width:400px;max-width:100%;box-sizing:border-box;
  display:flex;flex-direction:column;align-items:center;
  border-radius:24px;padding:28px 30px 24px;
  background:var(--c-surface);
  background:color-mix(in srgb, var(--c-surface) 84%, transparent);
  -webkit-backdrop-filter:blur(22px) saturate(1.5);backdrop-filter:blur(22px) saturate(1.5);
  border:1px solid var(--c-border);
  border-color:color-mix(in srgb, var(--c-border) 55%, transparent);
  box-shadow:0 18px 50px rgba(2,8,23,.28);
  animation:olsIn .25s ease;}
@keyframes olsIn{from{opacity:0;transform:translateY(14px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes olsShake{0%,100%{transform:translateX(0);}25%{transform:translateX(-7px);}75%{transform:translateX(7px);}}
@keyframes olsSpin{to{transform:rotate(360deg);}}
@media (prefers-reduced-motion: reduce){
  .ols-card{animation:none !important;}
  .ols-dots.err{animation:none !important;}
}

.ols-tenant{display:flex;align-items:center;gap:10px;margin-bottom:4px;}
.ols-brandmark{width:32px;height:32px;border-radius:9px;flex:none;
  display:flex;align-items:center;justify-content:center;}
/* Real brand asset, not a drawn glyph. Dark ink on light surfaces and the
   white variant on dark ones — a dark mark on a dark panel would vanish.
   Mirrors StationTopBar's .stb-brandmark treatment. */
.ols-brandmark img{width:100%;height:100%;object-fit:contain;display:block;}
:root[data-theme="dark"] .ols-brandmark img.mark-dark{display:none;}
.ols-brandmark img.mark-light{display:none;}
:root[data-theme="dark"] .ols-brandmark img.mark-light{display:block;}
.ols-tenant b{font-size:15px;font-weight:800;color:var(--c-ink);}
.ols-station{font-size:12px;font-weight:700;color:var(--c-muted);margin-bottom:4px;}
.ols-session{display:flex;align-items:center;gap:6px;margin-bottom:16px;font-size:11.5px;font-weight:700;color:var(--c-muted);}
.ols-session-dot{width:7px;height:7px;border-radius:50%;flex:none;background:var(--c-mutedLight);}
.ols-session-dot.open{background:var(--c-green);}
.ols-h{font-size:19px;font-weight:800;letter-spacing:-.3px;color:var(--c-ink);margin:0 0 14px;}

.ols-error{font-size:12.5px;font-weight:700;color:var(--c-redText);min-height:18px;margin:0 0 4px;
  text-align:center;}

/* PIN entry stays left-to-right even when the app is in Arabic.
   Digits read LTR in every language, and keypad position is muscle memory —
   mirroring it moves "1" to the top-right and swaps backspace with enter,
   which makes a cashier who knows their PIN by feel enter the wrong one.
   iOS and Android both keep their dial pads LTR under RTL locales. The
   surrounding screen (heading, brand, meta) stays RTL as normal. */
.ols-dots{display:flex;direction:ltr;gap:14px;margin:4px 0 20px;height:16px;align-items:center;}
.ols-dot{width:14px;height:14px;border-radius:50%;box-sizing:border-box;
  border:2px solid var(--c-mutedLight);background:transparent;transition:all .15s ease;}
.ols-dot.fill{background:var(--c-green);border-color:var(--c-green);transform:scale(1.15);}
.ols-dots.err{animation:olsShake .3s ease;}

.ols-keypad{display:grid;direction:ltr;grid-template-columns:repeat(3,1fr);gap:10px;width:100%;}
.ols-key{min-height:64px;border-radius:15px;cursor:pointer;touch-action:manipulation;
  background:var(--c-surface);color:var(--c-ink);border:1px solid var(--c-border);
  font-size:23px;font-weight:700;font-family:inherit;
  box-shadow:0 1px 2px rgba(2,8,23,.07);
  display:flex;align-items:center;justify-content:center;
  transition:transform .1s ease, background .12s ease;}
.ols-key:active{transform:scale(.93);background:var(--c-faint);}
.ols-key:disabled{opacity:.55;cursor:default;transform:none;}
.ols-key.fn{color:var(--c-muted);}
.ols-key.go{background:var(--c-green);border-color:var(--c-green);color:#fff;
  box-shadow:0 4px 12px color-mix(in srgb, var(--c-green) 40%, transparent);}
.ols-key.go:active{background:var(--c-greenDark);}
/* No RTL icon mirroring inside the keypad: the pad itself is now LTR, so the
   backspace and confirm glyphs already point the right way. Flipping them here
   would undo that. */

.ols-wordmark{margin-top:18px;font-size:12px;font-weight:800;letter-spacing:3px;
  text-transform:uppercase;color:var(--c-muted);opacity:.55;}
`;

// Both variants render; CSS shows whichever suits the active theme. Only
// the visible one carries alt text so a screen reader announces the brand
// once. Same pattern as StationTopBar.
const AqanaMark = (
    <>
        <img className="mark-dark" src="/aqana-mark.png" alt="Aqana" />
        <img className="mark-light" src="/aqana-mark-light.png" alt="" aria-hidden="true" />
    </>
);

const BackspaceIcon = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 4H8l-5 8 5 8h13a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1z" />
        <path d="m12 9 6 6M18 9l-6 6" />
    </svg>
);

const EnterIcon = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
);

function Spinner() {
    return (
        <svg style={{ animation: 'olsSpin .8s linear infinite' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-6.2-8.56" />
        </svg>
    );
}

// F12a-N task-n8 Fix D — module-level (outside the component, outside
// React entirely) PIN entry buffer. Defense-in-depth hardening: a
// component instance's own `useRef`/`useState` are scoped to THAT
// instance and are lost on any unmount+remount (a parent re-keying this
// component, or any other cause of a mid-entry remount), which reads to a
// cashier as "the PIN pad stopped registering taps" — digits fill, then
// silently vanish. Fix A/B (see backendBinding.js and index.js) remove the
// specific poisoned-auth-churn state this was hypothesized to correlate
// with in the field, but this buffer is cheap insurance against ANY other
// future cause of a mid-entry remount, independent of pinning down one
// specific trigger. A plain module-level `let` is safe here because there
// is only ever one physical PIN pad/lock screen live in the app at a time
// (one station, one operator entering a PIN) — mirrors that real-world
// invariant rather than introducing a shared store for a single global
// value. Cleared on every submit attempt (success or failure, see
// `submit` below) so a finished/abandoned entry never leaks into a LATER,
// unrelated lock episode (a different operator's PIN pre-filling on the
// next lock) — but deliberately NOT cleared on unmount, which is the
// entire point: a remount must be able to recover it.
let pendingPinBuffer = '';

// F12a-N task-n8 — formats a CashierSession.start_time for the session-
// status chip's optional "since" time. The backend stores this as a NAIVE
// UTC datetime (`Column(DateTime, default=datetime.utcnow)` — no tzinfo),
// which Pydantic serializes WITHOUT a trailing 'Z' (e.g.
// "2026-07-18T22:44:44.452306"). Parsing that string as-is with `new
// Date(...)` makes JS treat it as LOCAL time — silently wrong by the
// viewer's UTC offset. Appending 'Z' before parsing forces the correct UTC
// interpretation so `toLocaleTimeString()` then converts it to the
// viewer's real local time, per this codebase's "always local time" rule.
function formatSince(startTime) {
    if (!startTime) return '';
    try {
        const iso = /Z$|[+-]\d\d:?\d\d$/.test(startTime) ? startTime : `${startTime}Z`;
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
}

// Additive (default undefined -> renders nothing, same as no prop at all):
// { open: true, sessionId, startTime } | { open: false } from
// hooks/pos/useLockSessionStatus. Undefined also covers the "still loading"
// state deliberately — no spinner clutter on a lock screen.
export default function OperatorLockScreen({ stationLabel, unlocking, error, onSubmit, sessionStatus }) {
    const { getBrandName } = useAuth();
    const { t } = useLanguage();

    const [pin, setPin] = useState(() => pendingPinBuffer);
    const [shake, setShake] = useState(false);
    const busyRef = useRef(false);
    // Source of truth for the entered PIN — mirrored into state for the dots.
    // Kept in a ref so pressDigit stays side-effect-free inside setState (a
    // setState-updater side effect would double-submit under StrictMode).
    // Seeded from the module-level buffer (not always '') so a remounted
    // instance picks up mid-entry where the previous instance left off.
    const pinRef = useRef(pendingPinBuffer);

    const tenantName = (typeof getBrandName === 'function' && getBrandName()) || 'Aqana';

    const writePin = useCallback((value) => {
        pinRef.current = value;
        pendingPinBuffer = value;
        setPin(value);
    }, []);

    const submit = useCallback(async (pinValue) => {
        if (busyRef.current) return;
        busyRef.current = true;
        try {
            const result = await onSubmit(pinValue);
            if (!result) {
                setShake(true);
                setTimeout(() => setShake(false), 350);
            }
        } finally {
            // Clear on EVERY outcome — success, wrong PIN, or a thrown
            // error — a submitted PIN must never linger in the module
            // buffer for a later, unrelated lock episode to inherit.
            writePin('');
            busyRef.current = false;
        }
    }, [onSubmit, writePin]);

    const pressDigit = useCallback((digit) => {
        if (busyRef.current || pinRef.current.length >= PIN_LENGTH) return;
        const next = pinRef.current + digit;
        writePin(next);
        if (next.length === PIN_LENGTH) submit(next); // auto-submit on 6th digit
    }, [submit, writePin]);

    const pressBackspace = useCallback(() => {
        if (busyRef.current) return;
        writePin(pinRef.current.slice(0, -1));
    }, [writePin]);

    const pressEnter = useCallback(() => {
        if (busyRef.current || pinRef.current.length < PIN_LENGTH) return;
        submit(pinRef.current);
    }, [submit]);

    // F12a-N task-w — the keypad fires on `onPointerDown` instead of
    // `onClick`. Root cause: on the real WKWebView the lock screen's
    // session-status chip re-renders the tree between touchstart and
    // touchend on a live-updating station, and iOS declines to synthesize a
    // `click` event when the DOM mutates mid-gesture — taps land (Safari Web
    // Inspector confirms `touchstart` fires on `.ols-key`) but nothing ever
    // registers. `pointerdown` fires at touch-contact time, before any
    // gesture-completion synthesis, so it can't be swallowed the same way;
    // it also unifies mouse/touch/pen into one handler. `onClick` is
    // deliberately removed rather than kept as a fallback — coexistence
    // would double-fire on any input that supports both. `e.button` is
    // guarded so a non-primary mouse button (e.g. right-click) can't punch
    // a digit; touch/pen contacts report `button === 0`.
    const onPress = useCallback((fn) => (e) => {
        if (e && typeof e.button === 'number' && e.button !== 0) return;
        fn();
    }, []);

    // Physical keyboard support (tablets with keyboards / dev in browser).
    useEffect(() => {
        const onKeyDown = (e) => {
            if (/^[0-9]$/.test(e.key)) pressDigit(e.key);
            else if (e.key === 'Backspace') pressBackspace();
            else if (e.key === 'Enter') pressEnter();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [pressDigit, pressBackspace, pressEnter]);

    return createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 70 }} role="dialog" aria-modal="true" aria-label={t('Enter PIN to unlock')}>
            <AuroraBackground intensity="full">
                <style>{CSS}</style>
                <div className="ols-wrap">
                    <div className="ols-card">
                        <div className="ols-tenant">
                            <div className="ols-brandmark">{AqanaMark}</div>
                            <b>{tenantName}</b>
                        </div>
                        {stationLabel ? <div className="ols-station">{stationLabel}</div> : null}
                        {sessionStatus ? (
                            <div className="ols-session">
                                <span className={`ols-session-dot${sessionStatus.open ? ' open' : ''}`} aria-hidden="true" />
                                {sessionStatus.open ? (
                                    <span>
                                        {[sessionStatus.sessionId, t('open')].filter(Boolean).join(' · ')}
                                        {formatSince(sessionStatus.startTime) ? ` · ${formatSince(sessionStatus.startTime)}` : ''}
                                    </span>
                                ) : (
                                    <span>{t('no_active_session')}</span>
                                )}
                            </div>
                        ) : null}
                        <h1 className="ols-h">{t('Enter PIN to unlock')}</h1>
                        {error ? <p className="ols-error" role="alert">{error}</p> : null}
                        <div className={`ols-dots${shake ? ' err' : ''}`} aria-hidden="true">
                            {Array.from({ length: PIN_LENGTH }, (_, i) => (
                                <span key={i} className={`ols-dot${i < pin.length ? ' fill' : ''}`} />
                            ))}
                        </div>
                        <div className="ols-keypad">
                            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                                <button key={d} type="button" className="ols-key" disabled={!!unlocking} onPointerDown={onPress(() => pressDigit(d))}>
                                    {d}
                                </button>
                            ))}
                            <button type="button" className="ols-key fn" disabled={!!unlocking} onPointerDown={onPress(pressBackspace)} aria-label={t('delete')}>
                                {BackspaceIcon}
                            </button>
                            <button type="button" className="ols-key" disabled={!!unlocking} onPointerDown={onPress(() => pressDigit('0'))}>
                                0
                            </button>
                            <button type="button" className="ols-key go" disabled={!!unlocking} onPointerDown={onPress(pressEnter)} aria-label={t('confirm')}>
                                {unlocking ? <Spinner /> : EnterIcon}
                            </button>
                        </div>
                        <div className="ols-wordmark">Aqana</div>
                    </div>
                </div>
            </AuroraBackground>
        </div>,
        document.body
    );
}
